import { TrendPairData, PairStats } from '../types';
import { generatePairHash } from '../utils/hash';

/**
 * API Service Module
 * 
 * This module handles all communication with the backend API.
 * It provides functions for submitting user guesses and retrieving
 * statistics about other players' choices.
 */

/**
 * Get the appropriate base API URL based on environment
 * 
 * This function ensures our API calls work in both development and
 * production environments by using the right base URL.
 * 
 * In development: Uses the proxy setup in setupProxy.js
 * In production: Uses relative paths that resolve on the same domain
 * 
 * @returns The base URL for API endpoints
 */
const getApiUrl = (): string => {
  // We're using the same path for both environments
  // The proxy in development will route these correctly
  return '/api';
};

/**
 * Submit a user's guess to the backend API
 * 
 * This function sends the user's selection to the backend to be
 * recorded in the database. The selection includes information
 * about which pair was shown and which term the user selected.
 * 
 * @param pair - The pair of trends being compared
 * @param selectedTerm - The term the user chose
 * @param date - The date of the guess (YYYY-MM-DD format)
 * @returns Promise that resolves to the API response data
 */
export const submitGuess = async (
  pair: TrendPairData,
  selectedTerm: string,
  date: string
): Promise<any> => {
  // Generate a unique hash for this pair (used as an identifier)
  const pairHash = generatePairHash(pair.left, pair.right);
  const apiUrl = getApiUrl();
  
  try {
    // Prepare request payload
    const payload = {
      date,
      pairHash,
      leftTerm: pair.left,
      rightTerm: pair.right,
      selectedTerm
    };
    
    // Send POST request to the API
    const response = await fetch(`${apiUrl}/guesses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
    });
    
    // Handle error responses
    if (!response.ok) {
      // Try to extract detailed error message from response
      let errorDetails = '';
      try {
        const errorJson = await response.json();
        errorDetails = JSON.stringify(errorJson);
      } catch (e) {
        // If we can't parse JSON, use status text as fallback
        errorDetails = response.statusText;
      }
      
      throw new Error(`Failed to submit guess: ${response.status} ${errorDetails}`);
    }
    
    // Parse and return successful response
    const result = await response.json();
    return result;
  } catch (error) {
    // Log the error and re-throw it for the caller to handle
    console.error('Error submitting guess:', error);
    throw error;
  }
};

/**
 * Get statistics about how other users voted for a set of pairs
 * 
 * This function fetches statistics from the backend API about how 
 * other users have voted for the given pairs. This information is 
 * displayed to the user when they complete the game.
 * 
 * It handles:
 * - Generating hash identifiers for each pair
 * - Requesting statistics in a single batch
 * - Transforming the API response into a convenient format
 * - Providing fallback data if the API call fails
 * 
 * @param pairs - Array of trend pairs to get statistics for
 * @param date - The date to get statistics for (YYYY-MM-DD format)
 * @returns Record mapping pairHash → statistics
 */
export const getPairStats = async (
  pairs: TrendPairData[],
  date: string
): Promise<Record<string, PairStats>> => {
  const apiUrl = getApiUrl();
  
  try {
    // Generate unique hash identifiers for each pair
    const pairHashes = pairs.map(pair => 
      generatePairHash(pair.left, pair.right)
    );
    
    // Prepare request payload
    const requestPayload = {
      date,
      pairHashes
    };
    
    // Request statistics from the API
    const response = await fetch(`${apiUrl}/guesses/stats`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestPayload),
    });
    
    // Handle error responses
    if (!response.ok) {
      // Try to extract detailed error message
      let errorDetails = '';
      try {
        const errorJson = await response.json();
        errorDetails = JSON.stringify(errorJson);
      } catch (e) {
        // If we can't parse JSON, use status text as fallback
        errorDetails = response.statusText;
      }
      
      throw new Error(`Failed to get pair statistics: ${response.status} ${errorDetails}`);
    }
    
    // Parse successful response
    const stats: PairStats[] = await response.json();
    
    // Convert array to map for O(1) lookup by pairHash
    const statsMap: Record<string, PairStats> = {};
    stats.forEach(stat => {
      statsMap[stat.pairHash] = stat;
    });
    
    // Ensure all pairs have statistics entries
    // If a pair doesn't have statistics (new pair or no votes),
    // create a default entry with 50/50 split
    pairs.forEach(pair => {
      const pairHash = generatePairHash(pair.left, pair.right);
      if (!statsMap[pairHash]) {
        statsMap[pairHash] = {
          pairHash,
          leftTerm: pair.left,
          rightTerm: pair.right,
          leftPercentage: 50, // Default to 50/50 if no data
          rightPercentage: 50,
          totalVotes: 0
        };
      }
    });
    
    return statsMap;
  } catch (error) {
    console.error('Error getting pair statistics:', error);
    
    // Create fallback statistics for all pairs on error
    // This ensures the game can still display something even if the API fails
    const fallbackStats: Record<string, PairStats> = {};
    pairs.forEach(pair => {
      const pairHash = generatePairHash(pair.left, pair.right);
      fallbackStats[pairHash] = {
        pairHash,
        leftTerm: pair.left,
        rightTerm: pair.right,
        leftPercentage: 50, // Default to 50/50 on error
        rightPercentage: 50,
        totalVotes: 0
      };
    });
    
    return fallbackStats;
  }
}